import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { Howl, Howler } from 'howler';
import UserContext from '../../context/UserContext';

const SoundContext = createContext(null);

// Map semantic keys to sound files in public/sounds
const ASSETS = {
  // 'hangman:wrong': '/sounds/hangman-wrong.wav',
  // 'hangman:correct': '/sounds/hangman-correct.wav',
  // 'hangman:win': '/sounds/hangman-win.wav',
  // 'hangman:lose': '/sounds/hangman-lose.wav',
  // fallbacks to existing assets in public/sounds
  'ui:click': '/sounds/click.mp3',
  'ui:rolldie': '/sounds/roll_die.mp3',
  'ui:chatreceived': '/sounds/notification1.mp3',
  'ui:voicechatstarted': '/sounds/voicechatstarted.mp3',
  'ui:negative': '/sounds/negative.mp3',
  'ui:positive': '/sounds/positive.mp3',
  // Four-in-a-Row specific sounds (fallback to existing assets if you don't have custom files)
  'fourinrow:drop': '/sounds/fourinarow_drop.mp3',
  'snake:eat': '/sounds/positive.mp3',
  'gofish:gofish': '/sounds/negative.mp3',
  'gofish:allyours': '/sounds/positive.mp3',
  'gofish:setcomplete': '/sounds/positive.mp3',
  'ui:card': '/sounds/card.mp3',
  'ui:yourturn': '/sounds/yourturn.mp3',
  'snap:success': '/sounds/snap/snap.mp3',
  'snap:false': '/sounds/negative.mp3',
  'sinkingsships:hit': '/sounds/sinkingships_hit.mp3',
  'sinkingsships:miss': '/sounds/sinkingships_miss.mp3',
  'sinkingsships:miss1': '/sounds/sinkingships_miss1.mp3',
  'sinkingsships:sunk': '/sounds/sinkingships_sunk.mp3',
  // Snakes and Ladders
  'snakesandladders:ladder': '/sounds/snakesandladders/snakesandladders_ladder.mp3',
  'snakesandladders:snake': '/sounds/snakesandladders/snakesandladders_snake.mp3',
  // Wheel of Destiny: short tick as the pointer passes each slice edge
  'wheel:tick': '/sounds/shortclick.mp3',


};

export function SoundProvider({ children }) {
  // UserContext may not be provided at the time this provider mounts
  // (we sometimes wrap the app with SoundProvider before UserContext provider).
  // Safely read the context and allow the value to be either { user } or the user object directly.
  const userCtx = useContext(UserContext) || {};
  const user = userCtx.user !== undefined ? userCtx.user : userCtx;
  // Respect top-level user.audioEnabled (used by the profile modal), then
  // fall back to user.settings.enableSound for older users, otherwise default to true.
  const enabled = (() => {
    if (user && typeof user.audioEnabled === 'boolean') return Boolean(user.audioEnabled);
    if (user && user.settings && typeof user.settings.enableSound === 'boolean') return Boolean(user.settings.enableSound);
    return true;
  })();

  const soundsRef = useRef({});

  const getSound = (key) => {
    if (!(key in ASSETS)) {
      return null;
    }

    if (key in soundsRef.current) {
      return soundsRef.current[key];
    }

    try {
      const sound = new Howl({ src: [ASSETS[key]], volume: 1 });
      soundsRef.current[key] = sound;
      return sound;
    } catch (e) {
      soundsRef.current[key] = null;
      return null;
    }
  };

  useEffect(() => {
    // Apply global mute based on user setting
    Howler.mute(!enabled);
  // try { console.debug && console.debug('[SoundProvider] enabled=', enabled); } catch(e) {}
  }, [enabled]);

  const api = useMemo(() => ({
    play(key, opts = {}) {
      if (!enabled) return null;
      const s = getSound(key);
      if (!s) {
        return null;
      }
      try {
        if (typeof opts.volume === 'number') s.volume(opts.volume);
        if (opts.loop) s.loop(Boolean(opts.loop));
        return s.play();
      } catch (e) {
        return null;
      }
    },
    stop(key) {
      const s = getSound(key);
      if (!s) return;
      try { s.stop(); } catch (e) {}
    },
    preload(keys = []) {
      keys.forEach((key) => {
        const sound = getSound(key);
        try { sound && sound.load(); } catch (e) {}
      });
    },
    setMasterVolume(v) { try { Howler.volume(Number(v)); } catch (e) {} },
    enabled: () => enabled,
  }), [enabled]);

  return <SoundContext.Provider value={api}>{children}</SoundContext.Provider>;
}

export const useSound = () => useContext(SoundContext);

export default SoundProvider;
